import React, { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import TextField from '@mui/material/TextField/TextField';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Button from '@mui/material/Button/Button';
import IconButton from '@mui/material/IconButton/IconButton';
import Alert from '@mui/material/Alert/Alert';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { announceWinners, Award, HackathonPayload } from '../../services/hackathon.service';
import { IdeaScorecard } from '../../services/idea.service';
import { HelpLink } from '../HelpLink';

type RatedProject = { _id: string, title: string, categories: { [category: string]: IdeaScorecard } };

// the judges' averages added up across categories; what organizers were summing by hand
export const judgeTotal = (project: RatedProject) =>
    Object.values(project.categories).reduce((total, scores) => total + (scores?.judgeVoteAverage || 0), 0);

/**
 * Lets an organizer announce the winners: an overall winner plus any named awards ("Best Execution", ...).
 * Announcing closes voting and shows the results to everyone on the hackathon page.
 */
// what participants gave a team, added over categories (like the judges' total)
const participantTotalOf = (project: RatedProject) =>
    Object.values(project.categories || {}).reduce((total, scores) => total + (scores?.participantVoteAverage || 0), 0);

export const AnnounceWinners = ({ hackathon, projects, onAnnounced }: {
    hackathon: HackathonPayload,
    projects: RatedProject[],
    onAnnounced: () => void
}) => {
    const ranked = useMemo(() => [...projects].sort((a, b) => judgeTotal(b) - judgeTotal(a)), [projects]);
    const byParticipants = useMemo(() => [...projects].sort((a, b) => participantTotalOf(b) - participantTotalOf(a)), [projects]);
    const announced = hackathon.winners.length > 0;

    const initialAwards = (): Award[] => announced
        ? hackathon.winners.map(w => ({ ...w }))
        : [
            { category: 'Overall winner', ideaId: ranked[0]?._id || '' },
            // participants scored: suggest their favourite by their own totals (Oliver read it off the table by hand)
            ...(hackathon.participantScores && byParticipants[0] && participantTotalOf(byParticipants[0]) > 0
                ? [{ category: "Participants' pick", ideaId: byParticipants[0]._id }] : [])
        ];

    const [awards, setAwards] = useState<Award[]>(initialAwards);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    // follow the server's copy after an announcement (or one made in another tab); keyed by content because
    // the hackathon payload is rebuilt on every refresh, which would otherwise wipe the organizer's edits
    const winnersKey = JSON.stringify(hackathon.winners);
    useEffect(() => {
        setAwards(initialAwards());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [winnersKey]);

    // until the organizer picks, the overall winner follows the judges' ranking: scores arrive after the page opens,
    // and the first team in the lineup was being suggested instead (Oliver, run029)
    const [touched, setTouched] = useState(false);
    const top = ranked[0]?._id || '';
    const topParticipants = byParticipants[0] && participantTotalOf(byParticipants[0]) > 0 ? byParticipants[0]._id : '';
    useEffect(() => {
        if (!announced && !touched && top) {
            setAwards(initialAwards());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [top, topParticipants, announced, touched]);

    const update = (index: number, change: Partial<Award>) => {
        setTouched(true);
        setAwards(awards.map((award, i) => i === index ? { ...award, ...change } : award));
        setMessage('');
    };

    const save = (next: Award[]) => {
        if (next.some(a => !a.category.trim() || !a.ideaId)) {
            setError('Give every award a name and a team');
            return;
        }
        setSaving(true);
        setError('');
        // say on the Results card what decided each award: a top score (shared with others = a tie the organizers
        // broke, run032), else the organizers
        const round = (n: number) => Math.round(n * 100) / 100;
        const judgeBest = round(Math.max(0, ...projects.map(p => judgeTotal(p))));
        const participantBest = round(Math.max(0, ...projects.map(p => participantTotalOf(p))));
        const atTop = (total: (p: RatedProject) => number, best: number) => best > 0 ? projects.filter(p => round(total(p)) === best).map(p => p._id) : [];
        const judgesTop = atTop(judgeTotal, judgeBest);
        const participantsTop = atTop(participantTotalOf, participantBest);
        const basisOf = (award: Award): Pick<Award, 'basis' | 'tied'> => {
            const byParticipants = participantsTop.includes(award.ideaId);
            const byJudges = judgesTop.includes(award.ideaId);
            if (byParticipants && (/participant|audience|people/i.test(award.category) || !byJudges)) {
                return { basis: 'participants', tied: participantsTop.length > 1 };
            }
            if (byJudges) {
                return { basis: 'judges', tied: judgesTop.length > 1 };
            }
            return byParticipants ? { basis: 'participants', tied: participantsTop.length > 1 } : { basis: 'organizers' };
        };
        announceWinners(hackathon._id, next.map(award => ({ ...award, ...basisOf(award) }))).then(() => {
            setMessage(next.length > 0
                ? 'Announced! Everyone now sees the results on the hackathon page.'
                : 'Results taken back. Nothing is shown on the hackathon page.');
            onAnnounced();
        }, err => {
            setError(err?.response?.data?.message || 'Could not save the results');
        }).finally(() => setSaving(false));
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <EmojiEventsIcon color="secondary" />
                        <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>{announced ? 'Announced results' : 'Announce winners'}</Typography>
                        <HelpLink guide="winners">Announcing</HelpLink>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Once the judges agree, pick the overall winner and add any other awards. Announcing closes voting
                        and shows the results to everyone on the hackathon page. Teams are listed by their judges' total.
                    </Typography>
                    {awards.map((award, index) => (
                        <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <TextField
                                size="small"
                                label="Award"
                                value={award.category}
                                inputProps={{ maxLength: 60 }}
                                onChange={e => update(index, { category: e.target.value })}
                                sx={{ minWidth: 180 }} />
                            <TextField
                                select
                                size="small"
                                label="Team"
                                value={award.ideaId}
                                onChange={e => update(index, { ideaId: e.target.value })}
                                sx={{ flex: 1, minWidth: 0 }}>
                                {(/participant|audience|people/i.test(award.category) ? byParticipants : ranked).map(project => (
                                    <MenuItem key={project._id} value={project._id}>
                                        {project.title} · {/participant|audience|people/i.test(award.category)
                                            ? `participants' total ${participantTotalOf(project).toFixed(1)}`
                                            : `judges' total ${judgeTotal(project).toFixed(1)}`}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <IconButton aria-label={`Remove ${award.category || 'award'}`} onClick={() => setAwards(awards.filter((_, i) => i !== index))}>
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Stack>
                    ))}
                    {error && <Alert severity="error">{error}</Alert>}
                    {message && <Alert severity="success">{message}</Alert>}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button startIcon={<AddIcon />} disabled={awards.length >= 10} onClick={() => {
                            setAwards([...awards, { category: '', ideaId: '' }]);
                            setMessage('');
                        }}>Add award</Button>
                        <Button variant="contained" disabled={saving || awards.length === 0} onClick={() => save(awards)}>
                            {announced ? 'Update results' : 'Announce winners'}
                        </Button>
                        {announced && <Button color="error" disabled={saving} onClick={() => save([])}>Take back results</Button>}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
